import { Vector as VectorLayer } from 'ol/layer';
import { Cluster, Vector as VectorSource } from 'ol/source';
import { Feature } from 'ol';
import { Geometry } from 'ol/geom';
import { StyleLike } from 'ol/style/Style';
import { Severity, VegkartError, VegobjekttypeState } from '@/state';
import { useDispatch, useSelector } from '@/state/store';
import { isMultiFeatureCluster } from '@/utils/SpatialHelper';
import { featureStyle } from '../style/Style';
import { FeatureLayerType, FeatureLayerTypeKey } from './FeatureLayerType';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { MapContext } from '@/state/context/MapContext';
import { defaultColor, resolveColor } from '@/utils/ColorHelper';
import useMapLayer from './useMapLayer';
import { statePreferences } from '@/selectors/selectors';
import useClusterConfig from '../hooks/useClusterConfig';
import { handleError } from '@/actions/metaActions';
import { Vegobjekt } from '@/domain/vegobjekter/Vegobjekt';
import {
    splitCachedFeatures,
    updateVectorSourceFeatures,
    VegobjekterFeatureCache,
} from './VegobjekterFeatureCache';

interface FeaturesLayerProps {
    featureTypes: VegobjekttypeState[];
    zIndex: number;
}
export const FeaturesLayer = ({ zIndex, featureTypes }: FeaturesLayerProps) => {
    const { map } = useContext(MapContext);
    const preferences = useSelector(statePreferences);
    const dispatch = useDispatch();
    const source = useMemo(() => new VectorSource({}), []);
    const featureCache = useRef(new VegobjekterFeatureCache());
    const displayedFeatures = useRef<{
        points: Feature<Geometry>[];
        nonPoints: Feature<Geometry>[];
    }>({ points: [], nonPoints: [] });
    const clusterConfig = useClusterConfig(source);

    /**
     * Test if a feature is rendered with a bounding box smaller than 50px (area)
     * @param {Feature} feature the feature to test
     * @returns {boolean} true if the rendered area at current zoomlevel is less than 50px
     */
    const resolution = map.getView().getResolution() ?? Number.NaN;
    const geometries = useMemo(
        () =>
            featureTypes
                .filter(r => !!r.result && r.visible)
                .flatMap(r => {
                    if (r.categoryStates?.length > 0) {
                        return r.categoryStates
                            .filter(category => category.visible)
                            .flatMap(category => {
                                const result = category.result;
                                if (!result) return [];

                                return result.vegobjekter
                                    .filter(
                                        (
                                            feature
                                        ): feature is Vegobjekt & {
                                            geometri: NonNullable<Vegobjekt['geometri']>;
                                        } => Boolean(feature.geometri?.wkt)
                                    )
                                    .map(feature => ({
                                        feature,
                                        geometry: feature.geometri.wkt,
                                        color: category.color
                                            ? resolveColor(category.color)
                                            : defaultColor,
                                    }));
                            });
                    } else {
                        const result = r.result;
                        if (!result) return [];

                        return result.vegobjekter.flatMap(feature => {
                            if (!feature?.geometri?.wkt) return [];
                            const geometry =
                                preferences.useEgengeometriWhenAvailable &&
                                feature.geometri.egengeometri
                                    ? feature.geometri.wkt
                                    : feature.lokasjon?.geometri?.wkt;
                            if (!geometry) return [];

                            return [
                                {
                                    feature,
                                    geometry,
                                    color: r.color ? resolveColor(r.color) : defaultColor,
                                },
                            ];
                        });
                    }
                }),
        [featureTypes, preferences.useEgengeometriWhenAvailable]
    );

    const hasLoading = useMemo(
        () => featureTypes.some(r => r.visible && r.loading),
        [featureTypes]
    );

    useEffect(() => {
        if (hasLoading) return;
        const totalFeatures = featureTypes
            .filter(r => !!r.result && r.visible)
            .reduce((sum, r) => {
                if (r.categoryStates?.length > 0) {
                    return (
                        sum +
                        r.categoryStates
                            .filter(category => !!category.result && category.visible)
                            .reduce(
                                (catSum, category) =>
                                    catSum + (category.result?.vegobjekter.length || 0),
                                0
                            )
                    );
                } else {
                    return sum + (r.result?.vegobjekter.length || 0);
                }
            }, 0);

        if (totalFeatures > geometries.length) {
            dispatch(
                handleError(
                    new VegkartError(
                        'Ett eller flere vegobjekter mangler geometri',
                        {},
                        Severity.WARN
                    )
                )
            );
        }
    }, [featureTypes, geometries.length, dispatch, hasLoading]);

    const mapFeatures = useMemo(
        () =>
            geometries.flatMap(({ feature, geometry, color }) => {
                const cached = featureCache.current.get(feature, geometry, color);
                return cached ? [cached] : [];
            }),
        [geometries]
    );

    // Cluster source and layer are created once; distance is updated in place so zooming
    // does not tear down and re-add the layer on every view change.
    const [pointSource, clusterSource, clusterLayer] = useMemo(() => {
        const clusters = new Cluster(clusterConfig);

        clusters.on('addfeature', e => {
            if (e.feature) tagClusterFeatures(e.feature);
        });
        const layer = new VectorLayer({
            source: clusters,
            style: featureStyle as StyleLike,
        });

        return [source, clusters, layer];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [source]);

    useEffect(() => {
        clusterSource.setDistance(clusterConfig.distance);
    }, [clusterConfig.distance, clusterSource]);

    const [nonPointSource, nonPointLayer] = useMemo(() => {
        const source = new VectorSource();
        const layer = new VectorLayer({
            source,
            style: featureStyle as StyleLike,
        });
        return [source, layer];
    }, []);

    useMapLayer({ index: zIndex + 1, layer: clusterLayer });
    useMapLayer({ index: zIndex, layer: nonPointLayer });

    useEffect(() => {
        const { points, nonPoints } = splitCachedFeatures(
            mapFeatures,
            preferences.transformTinyGeometries,
            resolution
        );
        updateVectorSourceFeatures(pointSource, displayedFeatures.current.points, points);
        updateVectorSourceFeatures(
            nonPointSource,
            displayedFeatures.current.nonPoints,
            nonPoints
        );
        displayedFeatures.current = { points, nonPoints };
    }, [
        mapFeatures,
        nonPointSource,
        pointSource,
        preferences.transformTinyGeometries,
        resolution,
    ]);

    useEffect(() => {
        const cache = featureCache.current;
        return () => {
            pointSource.clear();
            nonPointSource.clear();
            cache.clear();
        };
    }, [nonPointSource, pointSource]);
    return null;
};

function tagClusterFeatures(feature: import('ol').Feature) {
    feature.set(
        FeatureLayerTypeKey,
        isMultiFeatureCluster(feature)
            ? FeatureLayerType.FEATURE_CLUSTER
            : FeatureLayerType.FEATURE_VEGOBJEKT
    );
}
